/**
 * controlPosture.js
 *
 * The dashboard's one control figure (GH #7262): the posture the daily
 * control-status pulse recorded with the shared control engine, read from
 * measurement-read?view=posture — the same record the compliance digest
 * reports. The dashboard never computes a control-compliance percentage of
 * its own (architecture.md §8.2, AD-1/AD-8).
 *
 *   postureDisplay(posture)  pure: a posture → what a widget renders
 *   useControlPosture()      the posture for this page load (one shared read)
 *   useMsrPublish()          msr 4.10a: the publish switch from the same read
 *   legacyPostureAllowed()   whether a retired posture figure may render
 *
 * Rules the display carries so no widget can get them wrong:
 *   - the figure is compliant ÷ controls in scope; a partial control is never
 *     counted towards it (TRYGGR_INTENT.md §5)
 *   - it is dated: "as of <day>", because it is the pulse's record, not a
 *     live evaluation (dashboard purpose.mustNeverClaim)
 *   - it is never labelled "applicable": the count is TCF controls in scope
 *     across enabled frameworks, not the SoA's "Applicable" figure
 *   - no usable pulse is "Not assessed" with the reason — never 0% or 100%
 */

import { useEffect, useState } from 'react';
import { getPostureRead } from '../../api/measurementApi.js';
import { fmtDate } from '../../utils/formatting.js';

export const POSTURE_LOADING = Object.freeze({ status: 'loading' });

/** @returns {{ assessed: boolean, loading: boolean, pct: number|null, compliant, inScope, partial, nonCompliant, day, asOf: string, reason: string|null, summary: string }} */
export function postureDisplay(posture) {
  if (posture && posture.status === 'loading') {
    return {
      assessed: false,
      loading: true,
      pct: null,
      compliant: null,
      inScope: null,
      partial: null,
      nonCompliant: null,
      day: null,
      asOf: '',
      reason: null,
      summary: 'Loading…',
    };
  }
  if (!posture || posture.status !== 'ok') {
    const reason = (posture && posture.reason) || 'the control-status record could not be read';
    return {
      assessed: false,
      loading: false,
      pct: null,
      compliant: null,
      inScope: null,
      partial: null,
      nonCompliant: null,
      day: (posture && posture.day) || null,
      asOf: '',
      reason,
      summary: `Not assessed — ${reason}`,
    };
  }
  const asOf = `as of ${fmtDate(posture.day)}`;
  return {
    assessed: true,
    loading: false,
    pct: posture.score,
    compliant: posture.compliant,
    inScope: posture.applicable,
    partial: posture.partial,
    nonCompliant: posture.nonCompliant,
    day: posture.day,
    asOf,
    reason: null,
    summary: `${posture.compliant} of ${posture.applicable} controls in scope compliant · ${posture.partial} partial · ${posture.nonCompliant} non-compliant · ${asOf}`,
  };
}

// One read per page load, shared by every widget. The pulse records once a
// day, so a longer-lived cache would only risk showing yesterday's record
// after today's has landed.
const TTL_MS = 10 * 60 * 1000;
let cached = null; // { at, promise }

const UNREADABLE = Object.freeze({
  posture: {
    status: 'not-assessed',
    reason: 'the control-status record could not be read',
    day: null,
  },
  publish: null,
});

function readPostureResponse() {
  if (!cached || Date.now() - cached.at > TTL_MS) {
    const promise = getPostureRead()
      .then((res) => ({ posture: res && res.posture, publish: (res && res.publish) || null }))
      .catch(() => UNREADABLE);
    cached = { at: Date.now(), promise };
  }
  return cached.promise;
}

function readPosture() {
  return readPostureResponse().then((r) => r.posture);
}

/** Test seam: forget the shared read. */
export function resetControlPostureCache() {
  cached = null;
}

export function useControlPosture() {
  const [posture, setPosture] = useState(POSTURE_LOADING);
  useEffect(() => {
    let live = true;
    readPosture().then((p) => {
      if (live) setPosture(p);
    });
    return () => {
      live = false;
    };
  }, []);
  return posture;
}

// ── msr 4.10a: the publish switch (docs/measurement/phase4-plan.md D26) ─────
//
// 'loading' | 'on' | 'off' | 'unavailable'. Cosmetic only — the server
// enforces the switch on every endpoint. Fail-closed for the retired figures:
// they render only on an explicit 'off' from the server, never while loading
// and never when the read failed or carried no switch, so a failed read on an
// environment where the switch is on can never bring the old score back.

export function publishStateOf(response) {
  const p = response && response.publish;
  if (!p || typeof p.enabled !== 'boolean') return 'unavailable';
  return p.enabled ? 'on' : 'off';
}

/** True only when the server said the switch is off (the pre-4.10 dashboard). */
export function legacyPostureAllowed(publish) {
  return publish === 'off';
}

// `enabled: false` is for a caller with no tenant session — the super-admin
// portal's user editor. measurement-read is tenant-JWT only and refuses a
// super-admin on every view, so the read there could only fail, and it failed
// loudly: a "Missing tenantId" banner on every open of the editor. It answers
// 'unavailable' without asking, which is what the refused read resolved to.
export function useMsrPublish({ enabled = true } = {}) {
  const [publish, setPublish] = useState(enabled ? 'loading' : 'unavailable');
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    readPostureResponse().then((r) => {
      if (live) setPublish(publishStateOf(r));
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return publish;
}
